// Dialogs and sheets (mockup ui.modal / ui.dialog / ui.sheet, element 077): the browser's own modal <dialog> traps focus,
// Esc · backdrop click · ✕ close, focus returns to what opened it. On phones (≤ 760 px) the CSS turns .sheet into a
// bottom sheet. Two ways to use it:
//   <Modal open={…} onClose={…} title="…" actions={[…]}>body</Modal>            (declarative)
//   const v = await modal({ title, body: (api) => …, actions: [{ label, value, primary }] })   (imperative, ui.modal)
import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { create } from 'zustand';
import { useT } from '../i18n';
import { reduceMotion } from '../lib/media';
import { cls } from '../lib/util';
import { Icon, type IconName } from './Icon';
import { raiseNotices } from './layers';

export interface ModalAction {
  label: ReactNode;
  /** What the promise / onClose receives (default: the label when it is a string). */
  value?: unknown;
  primary?: boolean;
  danger?: boolean;
  icon?: IconName;
  disabled?: boolean;
  /** Return false to keep the dialog open (validation). */
  onClick?: () => boolean | void;
}

export interface ModalProps {
  open: boolean;
  /** value = the action's value, or null for ✕ / Esc / backdrop. */
  onClose: (value: unknown) => void;
  title: ReactNode;
  kicker?: ReactNode;
  children?: ReactNode;
  actions?: readonly ModalAction[];
  wide?: boolean;
  /** Extra classes: 'sheet--panel' (sheet look), 'sheet--menu', screen-specific ones. */
  className?: string;
  initialFocus?: RefObject<HTMLElement | null>;
  /** Do not offer ✕/Esc/backdrop closing (steps that cannot be skipped). */
  required?: boolean;
}

type DialogLike = HTMLDialogElement & { showModal?: () => void; close?: () => void };
let openCount = 0;

export function Modal({ open, onClose, title, kicker, children, actions, wide, className, initialFocus, required }: ModalProps) {
  const t = useT();
  const titleId = useId();
  const ref = useRef<DialogLike>(null);
  const prevFocus = useRef<Element | null>(null);
  const [mounted, setMounted] = useState(open);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  if (open && !mounted) setMounted(true);

  useLayoutEffect(() => {
    const dlg = ref.current;
    if (!open || !dlg) return;
    prevFocus.current = document.activeElement;
    if (typeof dlg.showModal === 'function') {
      try {
        if (!dlg.open) dlg.showModal();
      } catch {
        dlg.setAttribute('open', '');
      }
    } else dlg.setAttribute('open', '');
    dlg.setAttribute('data-modal', '');
    openCount++;
    document.documentElement.classList.add('has-dlg');
    raiseNotices();
    const focusEl =
      initialFocus?.current ||
      dlg.querySelector<HTMLElement>('.sheet__body [autofocus]') ||
      dlg.querySelector<HTMLElement>('.sheet__body input, .sheet__body select, .sheet__body textarea, .sheet__body button, .sheet__body a[href], .sheet__body summary') ||
      dlg.querySelector<HTMLElement>('.x-btn');
    focusEl?.focus({ preventScroll: true });
    return () => {
      openCount = Math.max(0, openCount - 1);
      if (!openCount) document.documentElement.classList.remove('has-dlg');
      try {
        if (typeof dlg.close === 'function') dlg.close();
        else dlg.removeAttribute('open');
      } catch {
        dlg.removeAttribute('open');
      }
      dlg.removeAttribute('data-modal');
      raiseNotices();
      const back = prevFocus.current as HTMLElement | null;
      if (back && back.focus && document.contains(back)) back.focus({ preventScroll: true });
    };
    // initialFocus is read once per opening
  }, [open]);

  // keep the closing animation (.4 s) before unmounting
  useEffect(() => {
    if (open || !mounted) return;
    const tm = setTimeout(() => setMounted(false), reduceMotion() ? 0 : 420);
    return () => clearTimeout(tm);
  }, [open, mounted]);

  if (!mounted || typeof document === 'undefined') return null;
  const finish = (v: unknown) => {
    if (open) onCloseRef.current(v);
  };
  return createPortal(
    <dialog
      ref={ref}
      className={cls('sheet', wide && 'sheet--wide', className)}
      aria-labelledby={titleId}
      onCancel={(e) => {
        e.preventDefault();
        if (!required) finish(null);
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !required) finish(null);
      }}
    >
      <div className="sheet__head">
        <div>
          {kicker ? <p className="sheet__kicker">{kicker}</p> : null}
          <h2 className="sheet__title" id={titleId}>
            {title}
          </h2>
        </div>
        {required ? null : (
          <button className="x-btn" type="button" aria-label={t('common.close')} onClick={() => finish(null)}>
            <Icon name="x" />
          </button>
        )}
      </div>
      <div className="sheet__body">{children}</div>
      {actions && actions.length ? (
        <div className="sheet__foot">
          {actions.map((a, i) => (
            <button
              key={i}
              className={cls('btn', a.primary && 'btn--primary', a.danger && 'btn--danger')}
              type="button"
              disabled={a.disabled}
              onClick={() => {
                if (a.onClick && a.onClick() === false) return;
                finish(a.value === undefined ? (typeof a.label === 'string' ? a.label : true) : a.value);
              }}
            >
              {a.icon ? <Icon name={a.icon} /> : null}
              {a.label}
            </button>
          ))}
        </div>
      ) : null}
    </dialog>,
    document.body,
  );
}

// ───────────────────────── imperative: modal({...}) → Promise ─────────────────────────
export interface ModalApi {
  close(value?: unknown): void;
}
export interface ModalOptions extends Omit<ModalProps, 'open' | 'onClose' | 'children'> {
  body: ReactNode | ((api: ModalApi) => ReactNode);
}
interface OpenItem {
  id: number;
  open: boolean;
  opts: ModalOptions;
  resolve: (v: unknown) => void;
}
const useModalStore = create<{ items: OpenItem[] }>(() => ({ items: [] }));
let modalSeq = 0;

/** Open a dialog and wait for the pressed action's value (null when dismissed) — mockup ui.modal. */
export function modal(opts: ModalOptions): Promise<unknown> {
  return new Promise((resolve) => {
    const id = ++modalSeq;
    useModalStore.setState((s) => ({ items: [...s.items, { id, open: true, opts, resolve }] }));
  });
}
/** ui.sheet: bottom sheet on phones, centred dialog on PC. */
export const sheet = (opts: ModalOptions): Promise<unknown> => modal({ ...opts, className: cls('sheet--panel', opts.className) });

function closeItem(id: number, value: unknown) {
  const it = useModalStore.getState().items.find((x) => x.id === id);
  if (!it || !it.open) return;
  it.resolve(value);
  useModalStore.setState((s) => ({ items: s.items.map((x) => (x.id === id ? { ...x, open: false } : x)) }));
  setTimeout(() => useModalStore.setState((s) => ({ items: s.items.filter((x) => x.id !== id) })), reduceMotion() ? 0 : 450);
}

export function ModalHost() {
  const items = useModalStore((s) => s.items);
  return (
    <>
      {items.map((it) => {
        const api: ModalApi = { close: (v) => closeItem(it.id, v === undefined ? null : v) };
        const { body, ...rest } = it.opts;
        return (
          <Modal key={it.id} {...rest} open={it.open} onClose={(v) => closeItem(it.id, v)}>
            {typeof body === 'function' ? body(api) : body}
          </Modal>
        );
      })}
    </>
  );
}

/** Close every imperative dialog (sign-out, tests). */
export function closeAllModals(): void {
  useModalStore.getState().items.forEach((it) => closeItem(it.id, null));
}
